import moment from 'moment';

export const SCHEDULE_BUFFER_MINUTES = 30;
const SLOT_MS = SCHEDULE_BUFFER_MINUTES * 60 * 1000;

export function startOfDay(dt) {
    const d = new Date(dt);
    d.setHours(0, 0, 0, 0);
    return d;
}

export function sameCalendarDay(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

export function ceilToThirtyMinutes(dt) {
    const ms = dt.getTime();
    const rem = ms % SLOT_MS;
    return rem === 0 ? new Date(ms) : new Date(ms + (SLOT_MS - rem));
}

export function floorToThirtyMinutes(dt) {
    const ms = dt.getTime();
    return new Date(ms - (ms % SLOT_MS));
}

export function roundTripMinutes(oneWayMinutes) {
    return Math.max(0, oneWayMinutes || 0) * 2;
}

function locationPlaceId(loc) {
    return loc?.Dealership?.googlePlaceId || loc?.googlePlaceId || loc?.place_id || '';
}

function isSameOrigin(a, b) {
    if (!a || !b) {
        return false;
    }
    if (a.dealershipId && b.dealershipId && a.dealershipId === b.dealershipId) {
        return true;
    }
    const ap = locationPlaceId(a);
    const bp = locationPlaceId(b);
    return !!(ap && bp && ap === bp);
}

/** Drive time to the last stop before returning home. */
export function minutesToTurnaround(locations, fallbackMinutes = 0) {
    const locs = [...(locations || [])].sort((a, b) => (a.locationOrder ?? 0) - (b.locationOrder ?? 0));
    if (locs.length === 0) {
        return Math.max(0, fallbackMinutes || 0);
    }
    const origin = locs[0];
    const last = locs[locs.length - 1];
    const returnsHome = locs.length > 1 && isSameOrigin(origin, last);
    const lastOutbound = returnsHome && locs.length > 2 ? locs.length - 2 : locs.length - 1;
    let minutes = 0;
    for (let i = 0; i <= lastOutbound; i++) {
        minutes += Number(locs[i].estimatedHours) || 0;
    }
    return minutes > 0 ? minutes : Math.max(0, fallbackMinutes || 0);
}

/** Earliest delivery: now + drive time to the turnaround point + 30 minutes. */
export function minDeliveryDateTime(toTurnaroundMinutes, now = new Date()) {
    return new Date(now.getTime() + (Math.max(0, toTurnaroundMinutes || 0) + SCHEDULE_BUFFER_MINUTES) * 60000);
}

/**
 * Latest pickup: requested delivery minus (one-way drive time + 30 minutes).
 * "Plus 30 minutes" is a buffer added to the duration being subtracted.
 */
export function maxPickupDateTime(deliveryDateTime, oneWayMinutes) {
    const oneWay = Math.max(0, oneWayMinutes || 0);
    return new Date(deliveryDateTime.getTime() - (oneWay + SCHEDULE_BUFFER_MINUTES) * 60000);
}

export function hasScheduleSlot(minDateTime, maxDateTime) {
    return ceilToThirtyMinutes(minDateTime).getTime() <= floorToThirtyMinutes(maxDateTime).getTime();
}

export function endOfScheduleDay(dt) {
    const d = new Date(dt);
    d.setHours(23, 30, 0, 0);
    return d;
}

export function dateTimeFromDateAndTime(date, time) {
    if (!date || !time) {
        return null;
    }
    const parsed = moment(`${moment(date).format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD hh:mm A', true);
    return parsed.isValid() ? parsed.toDate() : null;
}

export function timesForDate(dt, minDate, maxDate) {
    const minBound = minDate ?? new Date();
    const day = startOfDay(dt);
    const lastSlot = endOfScheduleDay(dt);

    if (day < startOfDay(minBound)) {
        return [];
    }
    if (maxDate && day > startOfDay(maxDate)) {
        return [];
    }

    let start = day;
    let stop = lastSlot;

    if (sameCalendarDay(dt, minBound)) {
        start = ceilToThirtyMinutes(minBound);
    }
    if (maxDate && sameCalendarDay(dt, maxDate)) {
        stop = floorToThirtyMinutes(maxDate);
    }

    if (start > stop) {
        return [];
    }

    const lst = [];
    let cursor = start.getTime();
    const end = stop.getTime();
    let i = 0;
    while (cursor <= end) {
        lst.push({ index: i, value: moment(cursor).format('hh:mm A') });
        i += 1;
        cursor += SLOT_MS;
    }
    return lst;
}
